import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock, User, Eye, EyeOff, Loader2, Building2 } from 'lucide-react'
import { api } from '../api'

export default function Register({ onLogin }) {
  const nav = useNavigate()

  const [fullName, setFullName] = useState('')
  const [agencyName, setAgencyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [inviteToken] = useState(() => new URLSearchParams(window.location.hash.split('?')[1] || '').get('invite'))

  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [loading, setLoading] = useState(false)

  const isEmailValid = useMemo(() => {
    if (!email) return true
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  }, [email])

  const passwordStatus = useMemo(() => {
    const p = password || ''
    const checks = {
      length: p.length >= 8,
      upper: /[A-Z]/.test(p),
      lower: /[a-z]/.test(p),
      number: /[0-9]/.test(p)
    }
    const score = Object.values(checks).filter(Boolean).length
    return { checks, score }
  }, [password])

  const passwordsMatch = useMemo(() => {
    if (!confirmPassword) return true
    return password === confirmPassword
  }, [password, confirmPassword])

  const canSubmit = useMemo(() => (
    fullName.trim().length > 0 &&
    (inviteToken || agencyName.trim().length > 0) &&
    isEmailValid && email &&
    passwordStatus.checks.length &&
    passwordsMatch && confirmPassword &&
    !loading
  ), [fullName, agencyName, inviteToken, isEmailValid, email, passwordStatus, passwordsMatch, confirmPassword, loading])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!canSubmit) return
    setError(null); setSuccess(null); setLoading(true)
    try {
      await api('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, full_name: fullName, agency_name: agencyName, invite_token: inviteToken })
      })
      const loginData = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      })
      localStorage.setItem('accessToken', loginData.accessToken)
      setSuccess('Account created! Taking you to your dashboard…')
      onLogin?.()
      setTimeout(() => nav('/dashboard'), 700)
    } catch (err) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  function strengthInfo(score) {
    if (score <= 1) return { text: 'Weak', color: '#EF4444', width: '25%' }
    if (score === 2) return { text: 'Fair', color: '#F97316', width: '50%' }
    if (score === 3) return { text: 'Good', color: '#F59E0B', width: '75%' }
    return { text: 'Strong', color: '#10B981', width: '100%' }
  }
  const strength = strengthInfo(passwordStatus.score)

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .auth-root {
          min-height: 100vh;
          display: flex;
          font-family: 'DM Sans', sans-serif;
          background: #F8FAFF;
        }

        /* ── LEFT PANEL ── */
        .auth-left {
          width: 420px; flex-shrink: 0;
          background: linear-gradient(160deg, #1E3A8A 0%, #2563EB 55%, #3B82F6 100%);
          position: relative; overflow: hidden;
          display: flex; flex-direction: column;
          padding: 3rem;
        }
        @media (max-width: 960px) { .auth-left { display: none; } }

        .auth-left::before {
          content: '';
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 36px 36px;
        }
        .auth-left::after {
          content: '';
          position: absolute;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          bottom: -100px; right: -100px;
          pointer-events: none;
        }

        .auth-left-content { position: relative; z-index: 1; flex: 1; display: flex; flex-direction: column; }

        .auth-logo {
          font-family: 'Sora', sans-serif;
          font-size: 1.4rem; font-weight: 800; color: #fff;
          letter-spacing: -0.5px;
          display: flex; align-items: center; gap: 0.5rem;
          text-decoration: none;
        }
        .auth-logo-icon {
          width: 36px; height: 36px;
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1rem;
        }

        .auth-left-body { margin-top: auto; }

        .auth-left-tag {
          display: inline-block;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.9);
          font-size: 0.72rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.08em;
          padding: 0.3rem 0.8rem; border-radius: 100px;
          margin-bottom: 1.25rem;
        }

        .auth-left-body h2 {
          font-family: 'Sora', sans-serif;
          font-size: 1.8rem; font-weight: 800; color: #fff;
          line-height: 1.2; letter-spacing: -0.03em;
          margin-bottom: 1rem;
        }
        .auth-left-body p {
          color: rgba(255,255,255,0.7);
          font-size: 0.9rem; line-height: 1.65; max-width: 300px;
        }

        /* checklist */
        .auth-checklist {
          margin-top: 2rem;
          display: flex; flex-direction: column; gap: 0.75rem;
        }
        .auth-check-item {
          display: flex; align-items: center; gap: 0.75rem;
          font-size: 0.88rem; color: rgba(255,255,255,0.85);
        }
        .auth-check-icon {
          width: 22px; height: 22px; border-radius: 50%;
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.35);
          display: flex; align-items: center; justify-content: center;
          font-size: 0.7rem; flex-shrink: 0;
        }

        /* social proof row */
        .auth-social-proof {
          margin-top: 2.5rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.15);
          display: flex; align-items: center; gap: 1rem;
        }
        .auth-avatars { display: flex; }
        .auth-avatar-bubble {
          width: 30px; height: 30px; border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.5);
          background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 0.65rem; font-weight: 700; color: #fff;
          margin-left: -8px;
        }
        .auth-avatar-bubble:first-child { margin-left: 0; }
        .auth-social-text { font-size: 0.8rem; color: rgba(255,255,255,0.75); line-height: 1.4; }
        .auth-social-text strong { color: #fff; }

        /* ── RIGHT PANEL ── */
        .auth-right {
          flex: 1;
          display: flex; align-items: flex-start; justify-content: center;
          padding: 3rem 2rem;
          overflow-y: auto;
        }

        .auth-form-wrap { width: 100%; max-width: 440px; }

        .auth-mobile-logo {
          display: none;
          font-family: 'Sora', sans-serif;
          font-size: 1.2rem; font-weight: 800; color: #2563EB;
          margin-bottom: 2rem;
          align-items: center; gap: 0.5rem;
        }
        @media (max-width: 960px) { .auth-mobile-logo { display: flex; } }

        .auth-form-title {
          font-family: 'Sora', sans-serif;
          font-size: 1.65rem; font-weight: 800; color: #0F172A;
          letter-spacing: -0.03em; margin-bottom: 0.4rem;
        }
        .auth-form-sub { color: #64748B; font-size: 0.9rem; margin-bottom: 2rem; }

        /* invited banner */
        .invite-banner {
          background: #EFF6FF; border: 1.5px solid #BFDBFE;
          border-radius: 10px; padding: 0.8rem 1rem;
          font-size: 0.85rem; color: #1D4ED8;
          margin-bottom: 1.5rem;
          display: flex; align-items: center; gap: 0.6rem;
        }

        /* two-column row */
        .field-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        @media (max-width: 480px) { .field-2col { grid-template-columns: 1fr; } }

        .field-group { margin-bottom: 1.1rem; }
        .field-label {
          display: block;
          font-size: 0.82rem; font-weight: 600; color: #374151;
          margin-bottom: 0.4rem;
        }
        .field-wrap { position: relative; }
        .field-icon {
          position: absolute; left: 0.9rem; top: 50%; transform: translateY(-50%);
          color: #94A3B8; pointer-events: none;
          display: flex; align-items: center;
        }
        .field-input {
          width: 100%;
          padding: 0.78rem 0.9rem 0.78rem 2.5rem;
          border: 1.5px solid #E2E8F0; border-radius: 10px;
          font-size: 0.9rem; font-family: 'DM Sans', sans-serif;
          color: #0F172A; background: #fff; outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .field-input:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }
        .field-input.error { border-color: #F87171; box-shadow: 0 0 0 3px rgba(248,113,113,0.1); }
        .field-input-right {
          position: absolute; right: 0.9rem; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #94A3B8; display: flex; align-items: center; padding: 0;
        }
        .field-input-right:hover { color: #475569; }
        .field-error-text { font-size: 0.74rem; color: #EF4444; margin-top: 0.3rem; }

        /* password strength */
        .strength-row {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 0.5rem; margin-bottom: 0.3rem;
        }
        .strength-label { font-size: 0.74rem; color: #64748B; }
        .strength-value { font-size: 0.74rem; font-weight: 700; }
        .strength-bar-bg {
          height: 5px; background: #E2E8F0; border-radius: 100px;
          overflow: hidden; margin-bottom: 0.5rem;
        }
        .strength-bar-fill { height: 100%; border-radius: 100px; transition: width 0.4s, background 0.3s; }
        .pw-checks { display: grid; grid-template-columns: 1fr 1fr; gap: 0.3rem 0.75rem; }
        .pw-check {
          display: flex; align-items: center; gap: 0.35rem;
          font-size: 0.74rem; color: #94A3B8; transition: color 0.2s;
        }
        .pw-check.met { color: #10B981; }
        .pw-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: #CBD5E1; flex-shrink: 0; transition: background 0.2s;
        }
        .pw-dot.met { background: #10B981; }

        /* banners */
        .error-banner {
          background: #FEF2F2; border: 1.5px solid #FECACA;
          border-radius: 10px; padding: 0.8rem 1rem;
          font-size: 0.84rem; color: #DC2626;
          margin-bottom: 1.1rem;
          display: flex; align-items: center; gap: 0.5rem;
        }
        .success-banner {
          background: #F0FDF4; border: 1.5px solid #A7F3D0;
          border-radius: 10px; padding: 0.8rem 1rem;
          font-size: 0.84rem; color: #065F46;
          margin-bottom: 1.1rem;
          display: flex; align-items: center; gap: 0.5rem;
        }

        .submit-btn {
          width: 100%; padding: 0.9rem;
          background: #2563EB; color: #fff;
          border: none; border-radius: 10px;
          font-family: 'Sora', sans-serif;
          font-size: 0.95rem; font-weight: 700;
          cursor: pointer; margin-top: 0.5rem;
          display: flex; align-items: center; justify-content: center; gap: 0.5rem;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 14px rgba(37,99,235,0.3);
        }
        .submit-btn:hover:not(:disabled) {
          background: #1D4ED8; transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(37,99,235,0.4);
        }
        .submit-btn:disabled {
          background: #E2E8F0; color: #94A3B8;
          cursor: not-allowed; box-shadow: none; transform: none;
        }

        .auth-terms {
          font-size: 0.75rem; color: #94A3B8;
          text-align: center; margin-top: 1rem; line-height: 1.5;
        }
        .auth-terms a { color: #64748B; }

        .auth-switch {
          text-align: center; margin-top: 1.5rem;
          font-size: 0.88rem; color: #64748B;
        }
        .auth-switch a { color: #2563EB; font-weight: 700; text-decoration: none; }
        .auth-switch a:hover { text-decoration: underline; }

        .auth-footer {
          text-align: center; margin-top: 2rem;
          font-size: 0.75rem; color: #CBD5E1;
        }
        .auth-footer a { color: #94A3B8; text-decoration: none; }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="auth-root">
        {/* LEFT PANEL */}
        <div className="auth-left">
          <div className="auth-left-content">
            <Link to="/" className="auth-logo">
              <span className="auth-logo-icon">⚡</span>
              Prorately
            </Link>

            <div className="auth-left-body">
              <span className="auth-left-tag">14-day free trial</span>
              <h2>Set up in minutes.<br />Quote in seconds.</h2>
              <p>Join thousands of agents who've cut their quoting time in half with Prorately's AI-powered autofill and CRM.</p>

              <div className="auth-checklist">
                {[
                  'Chrome Extension for 40+ carrier portals',
                  'Full client CRM — unlimited records',
                  'Renewal alerts & policy tracking',
                  'Team collaboration tools',
                  'No credit card required to start',
                ].map(item => (
                  <div className="auth-check-item" key={item}>
                    <div className="auth-check-icon">✓</div>
                    {item}
                  </div>
                ))}
              </div>

              <div className="auth-social-proof">
                <div className="auth-avatars">
                  {['SM', 'JK', 'PN', 'AR'].map((a, i) => (
                    <div className="auth-avatar-bubble" key={i}>{a}</div>
                  ))}
                </div>
                <div className="auth-social-text">
                  <strong>12,400+ agents</strong> are already using Prorately
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="auth-right">
          <div className="auth-form-wrap">
            <div className="auth-mobile-logo">
              <span style={{ background: '#EFF6FF', borderRadius: 8, padding: '0.3rem 0.5rem', fontSize: '1rem' }}>⚡</span>
              Prorately
            </div>

            <h1 className="auth-form-title">
              {inviteToken ? 'Join your team' : 'Create your account'}
            </h1>
            <p className="auth-form-sub">
              {inviteToken ? "You've been invited to join an agency on Prorately." : 'Start your free 14-day trial. No credit card needed.'}
            </p>

            {inviteToken && (
              <div className="invite-banner">
                <span>🎉</span> You've been invited — your team is waiting!
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Name row */}
              <div className="field-2col">
                <div className="field-group">
                  <label className="field-label">Full name</label>
                  <div className="field-wrap">
                    <span className="field-icon"><User size={15} /></span>
                    <input className="field-input" type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Smith" autoComplete="name" required />
                  </div>
                </div>
                {!inviteToken && (
                  <div className="field-group">
                    <label className="field-label">Agency name</label>
                    <div className="field-wrap">
                      <span className="field-icon"><Building2 size={15} /></span>
                      <input className="field-input" type="text" value={agencyName} onChange={e => setAgencyName(e.target.value)} placeholder="Acme Insurance" required={!inviteToken} />
                    </div>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="field-group">
                <label className="field-label">Email address</label>
                <div className="field-wrap">
                  <span className="field-icon"><Mail size={15} /></span>
                  <input className={`field-input${!isEmailValid ? ' error' : ''}`} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@agency.com" autoComplete="email" required />
                </div>
                {!isEmailValid && <p className="field-error-text">Please enter a valid email address.</p>}
              </div>

              {/* Password */}
              <div className="field-group">
                <label className="field-label">Password</label>
                <div className="field-wrap">
                  <span className="field-icon"><Lock size={15} /></span>
                  <input className="field-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" required style={{ paddingRight: '2.5rem' }} />
                  <button type="button" className="field-input-right" onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {password && (
                  <>
                    <div className="strength-row">
                      <span className="strength-label">Password strength</span>
                      <span className="strength-value" style={{ color: strength.color }}>{strength.text}</span>
                    </div>
                    <div className="strength-bar-bg">
                      <div className="strength-bar-fill" style={{ width: strength.width, background: strength.color }} />
                    </div>
                    <div className="pw-checks">
                      {[
                        { key: 'length', label: '8+ characters' },
                        { key: 'upper', label: 'Uppercase letter' },
                        { key: 'lower', label: 'Lowercase letter' },
                        { key: 'number', label: 'Number' },
                      ].map(({ key, label }) => (
                        <div key={key} className={`pw-check${passwordStatus.checks[key] ? ' met' : ''}`}>
                          <div className={`pw-dot${passwordStatus.checks[key] ? ' met' : ''}`} />
                          {label}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Confirm Password */}
              <div className="field-group">
                <label className="field-label">Confirm password</label>
                <div className="field-wrap">
                  <span className="field-icon"><Lock size={15} /></span>
                  <input className={`field-input${!passwordsMatch ? ' error' : ''}`} type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" required style={{ paddingRight: '2.5rem' }} />
                  <button type="button" className="field-input-right" onClick={() => setShowConfirm(v => !v)}>
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {!passwordsMatch && <p className="field-error-text">Passwords do not match.</p>}
              </div>

              {error && <div className="error-banner"><span>⚠</span> {error}</div>}
              {success && <div className="success-banner"><span>✓</span> {success}</div>}

              <button type="submit" className="submit-btn" disabled={!canSubmit}>
                {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {loading ? 'Creating your account…' : 'Start free trial →'}
              </button>

              <p className="auth-terms">
                By creating an account, you agree to our{' '}
                <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
              </p>
            </form>

            <p className="auth-switch">
              Already have an account?{' '}
              <Link to="/login">Sign in</Link>
            </p>

            <p className="auth-footer">
              © {new Date().getFullYear()} Prorately. All rights reserved. ·{' '}
              <a href="#">Privacy</a> · <a href="#">Terms</a>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}